import { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useTranslation } from 'react-i18next';

import isIndexable from 'lib/isIndexable';
import OptOutHelper from 'lib/OptOutHelper';
import Layout from 'components/Layout';
import Checkbox from 'components/Shared/Checkbox';
import { colors } from 'components/styles/variables';
import { BaseButton } from 'components/Shared/Buttons';
import Link from 'next/link';

interface PageProps {
  indexable: boolean;
}

function DefaultCopy({
  isCaResident,
  toggleIsCaResident,
  isOptOutChecked,
  onSubmitOptOut,
}: {
  isCaResident: boolean;
  toggleIsCaResident: Dispatch<SetStateAction<boolean>>;
  isOptOutChecked: boolean;
  onSubmitOptOut: (e: React.ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className="ca-residency-checkbox">
        <Checkbox
          checked={isCaResident}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onClick={(e: React.ChangeEvent<HTMLInputElement>): void =>
            toggleIsCaResident(e.target.checked)
          }
        />
        <div className="ca-residency-checkbox-text">{t('ccpa.caResidencyCheckbox')}</div>
      </div>
      <div className="title">{t('ccpa.title')}</div>
      <div className="ca-privacy">{t('ccpa.caPrivacy')}</div>
      <div className="opt-out-subtext">{t('ccpa.optOutSubtext')}</div>
      <div className="opt-out-checkbox">
        <div className="opt-out-checkbox-text">{t('ccpa.optOutCheckbox')}</div>
        {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore */}
        <Checkbox checked={isOptOutChecked} onClick={onSubmitOptOut} />
      </div>
    </>
  );
}

function OptedOutContent(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className="title">{t('ccpa.title')}</div>
      <div className="ca-privacy">{t('ccpa.caPrivacyOptedOut')}</div>
      <div className="opt-out-subtext">{t('ccpa.optOutSubtextOptedOut')}</div>
      <Link href="/help">
        <BaseButton className="loading-button">{t('footer.contact')}</BaseButton>
      </Link>
    </>
  );
}

export default function CcpaOptOut({ indexable }: PageProps): JSX.Element {
  const { getLocalStorageOptOutValues, onOptOut } = OptOutHelper;
  const { t } = useTranslation();
  const [hasOptedOut, setHasOptedOut] = useState(false);
  const [isCaResident, toggleIsCaResident] = useState(false);
  const [isOptOutChecked, toggleOptOutChecked] = useState(false);

  useEffect(() => {
    setHasOptedOut(getLocalStorageOptOutValues().ccpaOptOut);
    toggleIsCaResident(getLocalStorageOptOutValues().isCaResident);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmitOptOut(event: React.ChangeEvent<HTMLInputElement>): void {
    toggleOptOutChecked(event.target.checked);
    setTimeout((): void => {
      onOptOut(isCaResident);
      setHasOptedOut(true);
    }, 1000);
  }

  return (
    <>
      <Layout
        title={t('main.meta.pageTitle')}
        description={t(`main.meta.pageTitle`)}
        indexed={indexable}
      >
        <div className="ccpa-container">
          <div className="text-container">
            {hasOptedOut ? (
              <OptedOutContent />
            ) : (
              <DefaultCopy
                isCaResident={isCaResident}
                toggleIsCaResident={toggleIsCaResident}
                isOptOutChecked={isOptOutChecked}
                onSubmitOptOut={onSubmitOptOut}
              />
            )}
          </div>
        </div>
      </Layout>
      <style>
        {`
          .ccpa-container {
            height: 560px;
            width: 100%;
            padding: 40px 16px 80px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          .text-container {
            max-width: 400px;
          }
          .title {
            font-size: 23px;
            font-weight: 600;
            letter-spacing: 0.24px;
            padding-top: 23px;
          }
          .ca-privacy, .opt-out-subtext {
            font-size: 16px;
            line-height: 1.4;
            letter-spacing: 0.2px;
          }
          .ca-privacy {
            padding-top: 16px;
          }
          .opt-out-subtext {
            padding: 24px 0 38px 0;
          }
          .opt-out-checkbox {
            display: flex;
            padding-bottom: 22px;
            border-bottom: 1px solid ${colors.G1};
          }
          .ca-residency-checkbox {
            display: flex;
            padding-bottom: 25px;
            border-bottom: 1px solid ${colors.G1};
          }
          .ca-residency-checkbox-text {
            font-size: 18px;
            font-weight: 600;
            line-height: 1.3;
            letter-spacing: 0.26px;
            margin-left: 12px;
          }
          .opt-out-checkbox-text {
            text-transform: uppercase;
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 0.25px;
            margin-right: 12px;
          }
        `}
      </style>
    </>
  );
}

export async function getServerSideProps({
  req,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<PageProps>> {
  const indexable = isIndexable(req);
  return { props: { indexable } };
}
